/**
 * SubscriptionSuccessModal.jsx
 * Celebratory modal shown when user returns from successful Stripe checkout
 */

import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Shield, Sparkles, Check, Crown, Zap, ArrowRight } from "lucide-react";
import { Button } from "../ui/button";
import confetti from "canvas-confetti";

export default function SubscriptionSuccessModal({ isOpen, onClose, userName, isFounder }) {
  const [showContent, setShowContent] = useState(false);

  useEffect(() => {
    if (isOpen) {
      // Delay content reveal for dramatic effect
      const timer = setTimeout(() => setShowContent(true), 300);
      
      // Fire confetti
      const fireConfetti = () => {
        const count = 200;
        const defaults = {
          origin: { y: 0.7 },
          zIndex: 9999,
        };

        function fire(particleRatio, opts) {
          confetti({
            ...defaults,
            ...opts,
            particleCount: Math.floor(count * particleRatio),
          });
        }

        // Gold confetti burst
        fire(0.25, {
          spread: 26,
          startVelocity: 55,
          colors: ['#D4AF37', '#FFD700', '#B8962E'],
        });
        fire(0.2, {
          spread: 60,
          colors: ['#D4AF37', '#FFD700', '#B8962E'],
        });
        fire(0.35, {
          spread: 100,
          decay: 0.91,
          scalar: 0.8,
          colors: ['#D4AF37', '#FFD700', '#B8962E', '#FFFFFF'],
        });
        fire(0.1, {
          spread: 120,
          startVelocity: 25,
          decay: 0.92,
          scalar: 1.2,
          colors: ['#D4AF37', '#FFD700'],
        });
        fire(0.1, {
          spread: 120,
          startVelocity: 45,
          colors: ['#D4AF37', '#FFFFFF'],
        });
      };

      fireConfetti();
      
      return () => clearTimeout(timer);
    } else {
      setShowContent(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const firstName = userName?.split(' ')[0] || 'there';

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        {/* Backdrop with gold glow */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/90 backdrop-blur-md"
          onClick={onClose}
        />

        {/* Modal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8, y: 40 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ type: "spring", damping: 20, stiffness: 300, delay: 0.1 }}
          className="relative z-10 w-full max-w-md mx-4"
          data-testid="subscription-success-modal"
        >
          {/* Glow effect behind card */}
          <div className="absolute inset-0 bg-gradient-to-b from-[#D4AF37]/20 to-transparent blur-3xl -z-10" />
          
          {/* Card */}
          <div className="bg-[#0A0A0A] border border-[#D4AF37]/40 rounded-2xl overflow-hidden shadow-2xl shadow-[#D4AF37]/10">
            {/* Header Glow Bar */}
            <div className="h-1.5 bg-gradient-to-r from-[#D4AF37] via-[#FFD700] to-[#D4AF37]" />
            
            {/* Close Button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-1.5 text-white/40 hover:text-white transition-colors z-20"
              data-testid="success-modal-close"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Content */}
            <div className="p-8 text-center">
              <AnimatePresence>
                {showContent && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2, duration: 0.5 }}
                  >
                    {/* Founder Badge Animation */}
                    {isFounder && (
                      <motion.div
                        initial={{ scale: 0, rotate: -180 }}
                        animate={{ scale: 1, rotate: 0 }}
                        transition={{ type: "spring", damping: 12, stiffness: 200, delay: 0.3 }}
                        className="relative mx-auto mb-6"
                      >
                        <div className="w-24 h-24 rounded-full bg-gradient-to-br from-[#D4AF37]/30 to-[#D4AF37]/5 border-2 border-[#D4AF37] flex items-center justify-center mx-auto">
                          <Crown className="w-12 h-12 text-[#D4AF37]" />
                        </div>
                        {/* Sparkle effects */}
                        <motion.div
                          animate={{ scale: [1, 1.2, 1], opacity: [0.5, 1, 0.5] }}
                          transition={{ duration: 2, repeat: Infinity }}
                          className="absolute -top-1 -right-1"
                        >
                          <Sparkles className="w-6 h-6 text-[#FFD700]" />
                        </motion.div>
                        <motion.div
                          animate={{ scale: [1, 1.2, 1], opacity: [0.5, 1, 0.5] }}
                          transition={{ duration: 2, repeat: Infinity, delay: 0.5 }}
                          className="absolute -bottom-1 -left-1"
                        >
                          <Sparkles className="w-5 h-5 text-[#FFD700]" />
                        </motion.div>
                      </motion.div>
                    )}

                    {/* Success Checkmark (non-founder) */}
                    {!isFounder && (
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ type: "spring", damping: 12, delay: 0.3 }}
                        className="w-20 h-20 rounded-full bg-emerald-500/20 border-2 border-emerald-500 flex items-center justify-center mx-auto mb-6"
                      >
                        <Check className="w-10 h-10 text-emerald-400" />
                      </motion.div>
                    )}

                    {/* Title */}
                    <motion.h2
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.5 }}
                      className="text-2xl font-bold text-white mb-2"
                    >
                      {isFounder ? "Your Free Trial Starts Now!" : "You're All Set!"}
                    </motion.h2>

                    {/* Subtitle */}
                    <motion.p
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.6 }}
                      className="text-white/50 text-sm mb-6"
                    >
                      {isFounder 
                        ? `${firstName}, your Founder membership is now active.`
                        : `${firstName}, your subscription is now active.`
                      }
                    </motion.p>

                    {/* Benefits */}
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.7 }}
                      className="bg-[#D4AF37]/5 border border-[#D4AF37]/20 rounded-xl p-4 mb-6"
                    >
                      <div className="space-y-3">
                        {isFounder ? (
                          <>
                            <div className="flex items-center gap-3 text-left">
                              <div className="w-8 h-8 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
                                <Crown className="w-4 h-4 text-[#D4AF37]" />
                              </div>
                              <div>
                                <p className="text-white text-sm font-medium">Founder Access Active</p>
                                <p className="text-white/40 text-xs">Full platform access from day one</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-3 text-left">
                              <div className="w-8 h-8 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
                                <Crown className="w-4 h-4 text-[#D4AF37]" />
                              </div>
                              <div>
                                <p className="text-white text-sm font-medium">Founder Badge</p>
                                <p className="text-white/40 text-xs">Displayed on your profile forever</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-3 text-left">
                              <div className="w-8 h-8 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
                                <Shield className="w-4 h-4 text-[#D4AF37]" />
                              </div>
                              <div>
                                <p className="text-white text-sm font-medium">$99/mo Locked Forever</p>
                                <p className="text-white/40 text-xs">$99/mo. Price won't increase, ever</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-3 text-left">
                              <div className="w-8 h-8 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
                                <Zap className="w-4 h-4 text-[#D4AF37]" />
                              </div>
                              <div>
                                <p className="text-white text-sm font-medium">Full Platform Access</p>
                                <p className="text-white/40 text-xs">All features, all portals included</p>
                              </div>
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="flex items-center gap-3 text-left">
                              <Check className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                              <span className="text-white/80 text-sm">Full platform access unlocked</span>
                            </div>
                            <div className="flex items-center gap-3 text-left">
                              <Check className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                              <span className="text-white/80 text-sm">AI deal analysis enabled</span>
                            </div>
                            <div className="flex items-center gap-3 text-left">
                              <Check className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                              <span className="text-white/80 text-sm">Execution Exchange marketplace</span>
                            </div>
                          </>
                        )}
                      </div>
                    </motion.div>

                    {/* CTA Button */}
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.9 }}
                    >
                      <Button
                        onClick={onClose}
                        className="w-full h-12 bg-[#D4AF37] hover:bg-[#B8962E] text-black font-bold text-base"
                        data-testid="success-modal-continue"
                      >
                        Start Building
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </motion.div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
